import React, { useEffect, useState } from "react";
import { DashboardTravelRequest } from "@/lib/models/dashboard";
import { TravelRequest } from "@/lib/request-trip/models/travel-request.model";
import { Calendar, MapPin, Users, Euro, Clock, Mail, Phone, FileText, User, AlertTriangle } from "lucide-react";

import { Alert, AlertDescription } from "@/components/ui/alert";
import { formatBudget, formatTravelDates, formatCreatedDate } from "@/components/utils/utils";
import { useI18n } from '@/hooks/useI18n';

// Tipo union per supportare entrambi i modelli
type TravelRequestType = DashboardTravelRequest | TravelRequest;

// Helper per normalizzare i valori tra i due tipi
const normalize = (tr: TravelRequestType) => ({
    contactFullName: 'contactFullName' in tr ? tr.contactFullName : `${tr.contact_first_name} ${tr.contact_last_name}`.trim(),
    contactEmail: 'contactEmail' in tr ? tr.contactEmail : tr.contact_email,
    contactPhone: 'contactPhone' in tr ? tr.contactPhone : tr.contact_phone,
    departurePoint: 'departurePoint' in tr ? tr.departurePoint : tr.departure_point,
    travelStartDate: 'travelStartDate' in tr ? tr.travelStartDate : tr.travel_start_date,
    travelEndDate: 'travelEndDate' in tr ? tr.travelEndDate : tr.travel_end_date,
    travelDatesFlexible: 'travelDatesFlexible' in tr ? tr.travelDatesFlexible : tr.travel_dates_flexible,
    totalGuests: 'totalGuests' in tr ? tr.totalGuests : tr.total_guests,
    guestsData: 'guestsData' in tr ? tr.guestsData : tr.guests_data,
    budgetMin: 'budgetMin' in tr ? tr.budgetMin : tr.budget_min,
    budgetMax: 'budgetMax' in tr ? tr.budgetMax : tr.budget_max,
    budgetFlexible: 'budgetFlexible' in tr ? tr.budgetFlexible : tr.budget_flexible,
    needsTransportation: 'needsTransportation' in tr ? tr.needsTransportation : tr.needs_transportation,
    additionalNotes: 'additionalNotes' in tr ? tr.additionalNotes : tr.additional_notes,
    createdAt: 'createdAt' in tr ? tr.createdAt : tr.created_at,
    // Per assignedAdvisor e preferredAdvisor - DashboardTravelRequest ha oggetti, TravelRequest ha users_detail
    assignedAdvisorName: 'assignedAdvisor' in tr && tr.assignedAdvisor 
        ? tr.assignedAdvisor.fullName 
        : ('users_detail' in tr && tr.users_detail?.assigned) ? `${tr.users_detail.assigned.first_name} ${tr.users_detail.assigned.last_name}` : null,
    preferredAdvisorName: 'preferredAdvisor' in tr && tr.preferredAdvisor 
        ? tr.preferredAdvisor.fullName 
        : ('users_detail' in tr && tr.users_detail?.preferred) ? `${tr.users_detail.preferred.first_name} ${tr.users_detail.preferred.last_name}` : null,
    hasAssignedAdvisor: 'assignedAdvisor' in tr ? !!tr.assignedAdvisor : ('users_detail' in tr && !!tr.users_detail?.assigned),
    hasPreferredAdvisor: 'preferredAdvisor' in tr ? !!tr.preferredAdvisor : ('users_detail' in tr && !!tr.users_detail?.preferred),
});

interface TravelRequestDetailsProps {
    travelRequest: TravelRequestType;
}

export const TravelRequestDetails: React.FC<TravelRequestDetailsProps> = ({
    travelRequest,
}) => {
    const { t } = useI18n();
    const data = normalize(travelRequest);
    
    const calculateExpiryDate = (createdAt: string) => {
        const created = new Date(createdAt);
        const expiry = new Date(created.getTime() + 48 * 60 * 60 * 1000); // 48 ore
        return expiry;
    };

    const calculateIsExpired = (status: string, createdAt: string) => {
        if (status !== 'pending') {
            return false;
        }
        const now = new Date();
        const expiry = calculateExpiryDate(createdAt);
        return now.getTime() > expiry.getTime();
    }

    return (
        <div className="py-4 px-6 space-y-4">

            {/* Box cliente */}
            <div className="rounded-lg bg-gray-50 border border-gray-200 px-5 py-4">
                <h6 className="tracking-wide text-gray-400 mb-2">{t('travelRequest.contacts')}</h6>
                <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
                    <span className="flex items-center gap-2 text-base font-semibold text-gray-900">
                        <User className="h-4 w-4 text-gray-500" />
                        {data.contactFullName}
                    </span>
                    <span className="flex items-center gap-2 text-sm text-gray-700">
                        <Mail className="h-3.5 w-3.5 text-gray-500" />
                        {data.contactEmail}
                    </span>
                    {data.contactPhone && (
                        <span className="flex items-center gap-2 text-sm text-gray-700">
                            <Phone className="h-3.5 w-3.5 text-gray-500" />
                            {data.contactPhone}
                        </span>
                    )}
                </div>
            </div>

            {/* Box unico: Destinazioni, Date, Budget, Trasporto */}
            <div className="rounded-lg border border-gray-100 bg-white p-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4">
                    {/* Destinazioni */}
                    <div className="space-y-1.5">
                        <div className="flex items-center gap-2">
                            <MapPin className="h-4 w-4 text-gray-500" />
                            <h6 className="tracking-wide text-gray-700">{t('itinerary.destinations')}</h6>
                        </div>
                        <p className="text-gray-900 font-medium">
                            {travelRequest.destinations.length > 0
                                ? travelRequest.destinations.join(", ")
                                : t('travelRequest.destinationTBD')
                            }
                        </p>
                        {data.departurePoint && (
                            <p className="text-sm text-gray-500">
                                {t('travelRequest.departureFrom')}: {data.departurePoint}
                            </p>
                        )}
                    </div>

                    {/* Date di viaggio */}
                    <div className="space-y-1.5">
                        <div className="flex items-center gap-2">
                            <Calendar className="h-4 w-4 text-gray-500" />
                            <h6 className="tracking-wide text-gray-700">{t('travelRequest.travelDates')}</h6>
                        </div>
                        <p className="text-gray-900 font-medium">
                            {formatTravelDates(data.travelStartDate, data.travelEndDate, {
                                fallbackText: t('travelRequest.datesToBeDefined'),
                                showYear: true
                            })}
                        </p>
                        {data.travelDatesFlexible && (
                            <p className="text-sm text-blue-600">{t('travelRequest.flexibleDates')}</p>
                        )}
                    </div>

                    {/* Budget */}
                    <div className="space-y-1.5">
                        <div className="flex items-center gap-2">
                            <Euro className="h-4 w-4 text-gray-500" />
                            <h6 className="tracking-wide text-gray-700">{t('travelRequest.budget')}</h6>
                        </div>
                        <p className="text-gray-900 font-medium">{formatBudget(data.budgetMin, data.budgetMax)}</p>
                        {data.budgetFlexible && (
                            <p className="text-sm text-blue-600">{t('travelRequest.flexibleBudget')}</p>
                        )}
                    </div>

                    {/* Trasporto */}
                    <div className="space-y-1.5">
                        <div className="flex items-center gap-2">
                            <Clock className="h-4 w-4 text-gray-500" />
                            <h6 className="tracking-wide text-gray-700">{t('travelRequest.transportation')}</h6>
                        </div>
                        <p className="text-gray-900 font-medium">
                            {data.needsTransportation ? t('travelRequest.transportationRequired') : t('travelRequest.transportationNotRequired')}
                        </p>
                    </div>
                </div>
            </div>

            {/* Riga Gruppo + Designer */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">

                {/* Gruppo */}
                <div className="rounded-lg border border-gray-100 bg-white p-4 space-y-1.5">
                    <div className="flex items-center gap-2">
                        <Users className="h-4 w-4 text-gray-500" />
                        <h6 className="tracking-wide text-gray-700">{t('travelRequest.group')}</h6>
                    </div>
                    <p className="text-gray-900 font-medium">
                        {data.totalGuests} {data.totalGuests === 1 ? t('travelRequest.person') : t('travelRequest.people')}
                    </p>
                    {data.guestsData && data.guestsData.length > 0 && (
                        <div className="space-y-1 pt-1">
                            <p className="text-xs text-gray-500">{t('travelRequest.guestsDetails')}:</p>
                            <div className="flex flex-wrap gap-1">
                                {data.guestsData.map((guest) => (
                                    <span
                                        key={guest.id}
                                        className="px-2 py-0.5 bg-gray-100 text-gray-700 rounded text-xs"
                                    >
                                        {guest.age} {t('travelRequest.years')}
                                    </span>
                                ))}
                            </div>
                        </div>
                    )}
                </div>

                {/* Designer */}
                <div className="rounded-lg border border-gray-100 bg-white p-4 space-y-1.5">
                    <div className="flex items-center gap-2">
                        <User className="h-4 w-4 text-gray-500" />
                        <h6 className="tracking-wide text-gray-700">{t('travelRequest.designer')}</h6>
                    </div>
                    {data.hasAssignedAdvisor ? (
                        <p className="text-gray-900 font-medium">{data.assignedAdvisorName}</p>
                    ) : (
                        <div className="space-y-0.5">
                            <p className="text-sm text-gray-500">{t('travelRequest.notAssigned')}</p>
                            {data.hasPreferredAdvisor ? (
                                <p className="text-sm text-gray-700">
                                    {t('travelRequest.preferred')}: <span className="font-medium">{data.preferredAdvisorName}</span>
                                </p>
                            ) : (
                                <p className="text-sm text-gray-500">{t('travelRequest.noPreference')}</p>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* Interessi - Solo se presenti */}
            {travelRequest.interests && travelRequest.interests.length > 0 && (
                <div className="rounded-lg border border-gray-100 bg-white p-4 space-y-2">
                    <div className="flex items-center gap-2">
                        <FileText className="h-4 w-4 text-gray-500" />
                        <h6 className="tracking-wide text-gray-700">{t('travelRequest.interests')}</h6>
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                        {travelRequest.interests.map((interest, index) => (
                            <span
                                key={index}
                                className="px-2.5 py-1 bg-gray-100 text-gray-800 rounded-full text-sm"
                            >
                                {interest}
                            </span>
                        ))}
                    </div>
                </div>
            )}

            {/* Note aggiuntive - Solo se presenti */}
            {data.additionalNotes && (
                <div className="rounded-lg border border-gray-100 bg-white p-4 space-y-2">
                    <div className="flex items-center gap-2">
                        <FileText className="h-4 w-4 text-gray-500" />
                        <h6 className="tracking-wide text-gray-700">{t('travelRequest.additionalNotes')}</h6>
                    </div>
                    <p className="text-gray-700 leading-relaxed text-sm">{data.additionalNotes}</p>
                </div>
            )}

            {/* Alert per la scadenza */}
            {travelRequest.isPending && (
                <Alert variant="info" className="mt-2">
                    <AlertTriangle className="h-4 w-4" />
                    <AlertDescription>
                        <div className="font-medium">
                            {t('travelRequest.expiresOn')} {formatCreatedDate(calculateExpiryDate(data.createdAt).toISOString(), { format: 'long' })} {t('travelRequest.at')} {calculateExpiryDate(data.createdAt).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}
                        </div>
                        <div className="mt-1 opacity-80">
                            {t('travelRequest.expiryMessage')}
                        </div>
                    </AlertDescription>
                </Alert>
            )}
            {calculateIsExpired(travelRequest.status, data.createdAt) && (
                <Alert variant="destructive" className="mt-2">
                    <AlertTriangle className="h-4 w-4" />
                    <AlertDescription>
                        <div className="font-medium">
                            {t('travelRequest.expiredOn')} {formatCreatedDate(calculateExpiryDate(data.createdAt).toISOString(), { format: 'long' })} {t('travelRequest.at')} {calculateExpiryDate(data.createdAt).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}
                        </div>
                        <div className="mt-1 opacity-80">
                            {t('travelRequest.expiredMessage')}
                        </div>
                    </AlertDescription>
                </Alert>
            )}
        </div>
    );
};

